import React, { useState, useEffect, useContext } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { AuthContext } from '../context/AuthContext';
import APIService from '../services/api';

interface ReferidosScreenProps {
  navigation: StackNavigationProp<any>;
}

interface Referido {
  id: number;
  nombre: string;
  telefono: string;
  correo: string;
  fecha_registro: string;
  monto_acumulado: string;
}

export const ReferidosScreen: React.FC<ReferidosScreenProps> = ({ navigation }) => {
  const { user } = useContext(AuthContext);
  const [referidos, setReferidos] = useState<Referido[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadReferidos();
  }, []);

  const loadReferidos = async () => {
    try {
      setLoading(true);
      const data = await APIService.getReferidos();
      setReferidos(data);
    } catch (error: any) {
      console.error('Error loading referidos:', error);
      Alert.alert(
        'Error',
        error.message || 'No se pudieron cargar los referidos'
      );
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadReferidos();
    setRefreshing(false);
  };

  const renderReferidoItem = ({ item }: { item: Referido }) => (
    <View style={styles.referidoCard}>
      <View style={styles.referidoHeader}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{item.nombre.charAt(0).toUpperCase()}</Text>
        </View>
        <View style={styles.referidoInfo}>
          <Text style={styles.referidoNombre}>{item.nombre}</Text>
          <Text style={styles.referidoTelefono}>📱 {item.telefono}</Text>
          {item.correo && (
            <Text style={styles.referidoCorreo}>📧 {item.correo}</Text>
          )}
        </View>
      </View>
      
      <View style={styles.referidoFooter}>
        <View style={styles.footerItem}>
          <Text style={styles.footerLabel}>Registrado</Text>
          <Text style={styles.footerValue}>
            {new Date(item.fecha_registro).toLocaleDateString('es-GT', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </Text>
        </View>
        <View style={styles.footerItem}>
          <Text style={styles.footerLabel}>Total gastado</Text>
          <Text style={[styles.footerValue, styles.montoText]}>
            Q{parseFloat(item.monto_acumulado || '0').toFixed(2)}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyIcon}>👥</Text>
      <Text style={styles.emptyTitle}>Sin referidos aún</Text>
      <Text style={styles.emptyText}>
        Invita a tus amigos a registrarse usando tu ID de cliente
      </Text>
      {user?.id && (
        <View style={styles.userIdContainer}>
          <Text style={styles.userIdLabel}>Tu ID de referido:</Text>
          <Text style={styles.userIdValue}>{user.id}</Text>
        </View>
      )}
    </View>
  );

  if (loading && !refreshing) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#e74c3c" />
          <Text style={styles.loadingText}>Cargando referidos...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.backButtonText}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Mis Referidos</Text>
        <View style={styles.headerRight}>
          <Text style={styles.totalBadge}>{referidos.length}</Text>
        </View>
      </View>

      {/* Summary Card */}
      <View style={styles.summaryCard}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryNumber}>{referidos.length}</Text>
          <Text style={styles.summaryLabel}>Total Referidos</Text>
        </View>
        <View style={styles.summaryDivider} />
        <View style={styles.summaryItem}>
          <Text style={styles.summaryNumber}>
            Q{referidos.reduce((sum, ref) => sum + parseFloat(ref.monto_acumulado || '0'), 0).toFixed(2)}
          </Text>
          <Text style={styles.summaryLabel}>Compras Totales</Text>
        </View>
      </View>

      {/* Lista de referidos */}
      <FlatList
        data={referidos}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderReferidoItem}
        ListEmptyComponent={renderEmptyState}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#e74c3c']}
            tintColor="#e74c3c"
          />
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#7f8c8d',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#e74c3c',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonText: {
    fontSize: 32,
    color: 'white',
    fontWeight: 'bold',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
    textAlign: 'center',
  },
  headerRight: {
    width: 40,
    alignItems: 'flex-end',
  },
  totalBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  summaryCard: {
    flexDirection: 'row',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 16,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryItem: {
    flex: 1,
    alignItems: 'center',
  },
  summaryNumber: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#e74c3c',
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  summaryDivider: {
    width: 1,
    backgroundColor: '#e9ecef',
    marginHorizontal: 16,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  referidoCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  referidoHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#e74c3c',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 22,
    fontWeight: 'bold',
    color: 'white',
  },
  referidoInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  referidoNombre: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  referidoTelefono: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 2,
  },
  referidoCorreo: {
    fontSize: 13,
    color: '#95a5a6',
  },
  referidoFooter: {
    flexDirection: 'row',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  footerItem: {
    flex: 1,
  },
  footerLabel: {
    fontSize: 12,
    color: '#95a5a6',
    marginBottom: 4,
  },
  footerValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  montoText: {
    color: '#27ae60',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 60,
    paddingHorizontal: 40,
  },
  emptyIcon: {
    fontSize: 80,
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 12,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  userIdContainer: {
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  userIdLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
  },
  userIdValue: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
});
